import { Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import type { FC } from "react";
import type { Control } from "react-hook-form";
import { useAgents } from "../admin/agents/useAgents";
import FormDatePicker from "../components/formComponents/FormDatePicker";
import FormSelect from "../components/formComponents/FormSelect";
import FormTextField from "../components/formComponents/FormTextField";
import TagSelect from "../components/tagSelect/TagSelect";
import type { CustomerWithFiles } from "./AddressDetails";
import AddressDetails from "./AddressDetails";
import ContactPersons from "./ContactPersons";
import { CUSTOMER_STATUS_VALUES } from "./customerStatus";
import type { CustomerStatus } from "./customersApi";
import { useCustomerTagsByCategory } from "./useCustomerTagsByCategory";

interface Props {
  readonly control: Control<CustomerFormState>;
  readonly tags: TagsState;
  readonly setTags: (newTags: TagsState) => void;
  readonly readonly?: boolean;
  readonly accountManagerDisabled?: boolean;
  readonly filesEnabled?: boolean;
  readonly customer?: CustomerWithFiles;
}

export interface CustomerFormState {
  name: string;
  accountManager: string | null;
  status: CustomerStatus;
  contactDate: Dayjs | null;
  meetingDate: Dayjs | null;
  info: string;
  nip: string;
  contacts: {
    name: string;
    phone: string;
    email: string;
    loginEmail: string;
    position: string;
    privileges: {
      canOrderCalculation: boolean;
      canCreateCalculationFromProducts: boolean;
    };
  }[];
  invoiceEmail: string;
  publicContactName: string;
  publicContactAddress: string;
  publicContactEmail: string;
  publicContactPhone: string;
  publicContactWebpage: string;
  publicContactGooglePlaceId: string;
  publicContactCity: string;
  publicContactvoivodeship: string;
  travelPayStatus: CustomerStatus;
  travelPayAccountManager: string | null;
}

export interface TagsState {
  readonly marketSegment: { readonly category: string; readonly value: string }[];
}

const CustomerEdit: FC<Props> = ({
  control,
  tags,
  setTags,
  readonly,
  accountManagerDisabled,
  filesEnabled,
  customer,
}) => {
  const { agents } = useAgents();
  const customerTagsByCategory = useCustomerTagsByCategory();

  return (
    <Grid size={12} container spacing={2}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <FormTextField
          label="Nazwa"
          control={control}
          name="name"
          rules={{ required: { value: true, message: "Nazwa klienta nie może być pusta" } }}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormDatePicker fullWidth label="Data kontaktu" readonly control={control} name="contactDate" />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormDatePicker fullWidth label="Data spotkania" readonly control={control} name="meetingDate" />
      </Grid>
      <Grid size={6}>
        <TagSelect
          availableTags={customerTagsByCategory}
          maxVisibleTags={2}
          tags={tags.marketSegment}
          label="Segment rynku"
          onChange={(marketSegment) => setTags({ ...tags, marketSegment })}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormSelect
          label="Opiekun"
          control={control}
          options={agents?.map((a) => ({ value: a.id, label: a.name })) ?? []}
          name="accountManager"
          disabled={accountManagerDisabled}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormSelect
          options={CUSTOMER_STATUS_VALUES.map((value) => ({ value, label: value }))}
          name="status"
          control={control}
          label="Status"
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormSelect
          label="Opiekun TravelPay"
          control={control}
          options={agents?.map((a) => ({ value: a.id, label: a.name })) ?? []}
          name="travelPayAccountManager"
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 3 }}>
        <FormSelect
          options={CUSTOMER_STATUS_VALUES.map((value) => ({ value, label: value }))}
          name="travelPayStatus"
          control={control}
          label="Status TravelPay"
        />
      </Grid>
      <Grid size={12}>
        <ContactPersons control={control} />
      </Grid>
      <Grid size={12}>
        <AddressDetails control={control} readonly={readonly} filesEnabled={filesEnabled} customer={customer} />
      </Grid>
    </Grid>
  );
};

export default CustomerEdit;
